import { useEffect, useRef, useState } from "react";
import BaseObject from "./BaseObject";
import useFrozen from "./useFrozen";

export function useOnChangeWatcher(
  obj: BaseObject | undefined,
  callback?: (changedProperty: string | any | undefined, changedObject: BaseObject, source?: string) => boolean,
) {
  const frozen = useFrozen();

  const update = useRef(0);
  const [, setRedraw] = useState(0);

  const dirtyRef = useRef(false);
  const frameRef = useRef<number | null>(null);

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  if (obj && !obj?.onChange) {
    console.error("useOnChangeWatcher: Object has NO onChange. Not a BaseObject?", obj);
    obj = undefined;
  }

  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  const flushRedraw = () => {
    frameRef.current = null;

    // Während Freeze nichts an React weitergeben.
    if (frozenRef.current) {
      return;
    }

    // Seit dem letzten Render keine Änderung.
    if (!dirtyRef.current) {
      return;
    }

    dirtyRef.current = false;

    update.current += 1;
    setRedraw(update.current);
  };

  const requestRedraw = () => {
    // Die Darstellung ist ab jetzt veraltet.
    dirtyRef.current = true;

    // Während Freeze nur dirty merken.
    if (frozenRef.current) {
      return;
    }

    // Für diesen Browser-Frame ist bereits
    // ein Redraw geplant.
    if (frameRef.current !== null) {
      return;
    }

    frameRef.current = requestAnimationFrame(flushRedraw);
  };

  useEffect(() => {
    if (!obj) {
      return;
    }

    const handle = obj.onChange.subscribe(
      (changedObject: BaseObject, changedProperty: string | any | undefined, source?: string) => {
        let autoRedraw = true;

        if (callbackRef.current) {
          autoRedraw = callbackRef.current(changedProperty, changedObject, source);
        }

        if (autoRedraw) {
          requestRedraw();
        }
      },
    );

    return () => {
      obj.onChange.unsubscribe(handle);

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [obj]);

  // Freeze aufgehoben:
  // Falls währenddessen Änderungen aufgelaufen sind,
  // genau EIN Redraw nachholen.
  useEffect(() => {
    if (!frozen && dirtyRef.current && frameRef.current === null) {
      frameRef.current = requestAnimationFrame(flushRedraw);
    }
  }, [frozen]);

  return [update, ""];
}
